import CONFIG from '@/constants/config.js';
import { createCoarseLocationDetector } from '@/utils/coarse-location.mjs';
import { isMobileBrowser } from '@/utils/platform.mjs';

const state = {
    status: CONFIG.GPS_STATUS_INACTIVE,
    // last known device position {lat, lng}, filled by requestPosition (event list distance sort)
    position: null,
    // coarse-only permission heuristic (utils/coarse-location.mjs); not persisted, so the
    // dialog comes back once per page load
    coarseLocationDetected: false,
    coarseDialogShown: false
}

// one detector per page load, created on the first reported fix
let coarseDetector = null;

function getCoarseDetector(commit) {
    if (coarseDetector === null) {
        coarseDetector = createCoarseLocationDetector({
            thresholdM: CONFIG.GPS_COARSE_ACCURACY_M,
            confirmMs: CONFIG.GPS_COARSE_CONFIRM_MS,
            onCoarse: () => commit('SET_COARSE_LOCATION_DETECTED', true)
        });
    }
    return coarseDetector;
}

const getters = {
    gpsStatus(state) {
        return state.status;
    },
    isGpsDenied(state) {
        return state.status === CONFIG.GPS_STATUS_DENIED;
    },
    hasGpsProblem(state) {
        return state.status === CONFIG.GPS_STATUS_DENIED || state.status === CONFIG.GPS_STATUS_UNAVAILABLE;
    },
    position(state) {
        return state.position;
    },
    coarseDialogPending(state) {
        return state.coarseLocationDetected && !state.coarseDialogShown;
    }
}

const actions = {
    // One-shot position request. Resolves {lat, lng}; rejects with the browser error and
    // records denied/unavailable in the status (the map keeps its own watchPosition tracking).
    requestPosition({ commit }) {
        return new Promise((resolve, reject) => {
            if (typeof navigator === 'undefined' || !navigator.geolocation) {
                commit('SET_GPS_STATUS', CONFIG.GPS_STATUS_UNAVAILABLE);
                reject(new Error('geolocation unavailable'));
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (result) => {
                    const position = { lat: result.coords.latitude, lng: result.coords.longitude };
                    commit('SET_POSITION', position);
                    resolve(position);
                },
                (error) => {
                    // PERMISSION_DENIED = 1
                    commit('SET_GPS_STATUS', error && error.code === 1 ? CONFIG.GPS_STATUS_DENIED : CONFIG.GPS_STATUS_UNAVAILABLE);
                    reject(error);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
            );
        });
    },
    // Feeds a watchPosition fix into the coarse-location heuristic. Desktop browsers are skipped:
    // their IP-based positions are just as inaccurate without any coarse permission.
    reportAccuracy({ commit }, accuracy) {
        if (!isMobileBrowser()) {
            return;
        }
        getCoarseDetector(commit).onFix(accuracy);
    },
    stopCoarseDetection() {
        if (coarseDetector !== null) {
            coarseDetector.stop();
        }
    }
}

const mutations = {
    SET_GPS_STATUS(state, status) {
        state.status = status;
    },
    SET_POSITION(state, position) {
        state.position = position;
    },
    SET_COARSE_LOCATION_DETECTED(state, detected) {
        state.coarseLocationDetected = detected;
    },
    SET_COARSE_DIALOG_SHOWN(state) {
        state.coarseDialogShown = true;
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
